import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waitlist() {
  const [email, setEmail] = React.useState("");
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [count, setCount] = React.useState(null);

  // Fetch live waitlist count on mount
  React.useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/waitlist/count`)
      .then((res) => {
        if (!cancelled) setCount(res.data?.count ?? 0);
      })
      .catch(() => {
        /* silent — counter just won't render */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email: email.trim().toLowerCase(),
        source: "landing_waitlist_section",
      });
      // Only bump counter if it was a fresh subscribe (not already_subscribed)
      if (res.data?.status === "subscribed" && typeof count === "number") {
        setCount(count + 1);
      }
      setSubmitted(true);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Something went wrong — please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-cta-section"
      className="relative bg-[#1E4D55] text-white overflow-hidden"
    >
      {/* Background pattern */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="lg:col-span-7"
          >
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-white/70">
              Pilot soft launch — Week 5
            </p>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-[1]">
              Be first in the pilot.
              <br />
              <span className="text-[#F5A623]">First job is on us.</span>
            </h2>
            <p className="mt-5 max-w-xl text-white/80 text-lg">
              We&apos;re letting in a small batch of customers and verified Luton
              van drivers across Leeds, Manchester, Birmingham &amp; London
              before public rollout. Drop your email — we&apos;ll reach out
              within 48 hours.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            {submitted ? (
              <div
                data-testid="waitlist-success"
                className="bg-white text-[#0A0A0A] p-6 border border-white flex items-start gap-4"
              >
                <CheckCircle2 size={24} className="text-[#5BB247] shrink-0 mt-0.5" />
                <div>
                  <p className="font-display text-lg font-bold tracking-tight">
                    You're on the list.
                  </p>
                  <p className="text-sm text-[#475569] mt-1">
                    We'll be in touch within 48 hours with next steps.
                  </p>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="waitlist-form"
                className="bg-white p-2 flex flex-col sm:flex-row gap-2"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="waitlist-email-input"
                  className="flex-1 px-4 py-4 text-[#0A0A0A] placeholder-[#475569] bg-transparent border-0 focus:outline-none focus:ring-2 focus:ring-[#1E4D55] text-sm"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="waitlist-submit-button"
                  className="group inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white hover:bg-[#5BB247] transition-colors px-6 py-4 font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span>Joining…</span>
                    </>
                  ) : (
                    <>
                      <span>Join waitlist</span>
                      <ArrowUpRight
                        size={18}
                        className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </>
                  )}
                </button>
              </form>
            )}

            {error && !submitted && (
              <p
                data-testid="waitlist-error"
                className="mt-3 text-xs text-[#F5A623] font-medium"
              >
                {error}
              </p>
            )}

            {/* Live social-proof counter */}
            {typeof count === "number" && count > 0 && (
              <motion.div
                key={count}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                data-testid="waitlist-counter"
                className="mt-5 flex items-center gap-2.5 text-sm"
              >
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#5BB247] opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#5BB247]" />
                </span>
                <span className="text-white/90">
                  <span
                    className="font-display font-bold text-white"
                    data-testid="waitlist-count-number"
                  >
                    {count.toLocaleString("en-GB")}
                  </span>{" "}
                  {count === 1 ? "person is" : "people are"} already on the list.
                </span>
              </motion.div>
            )}

            <p className="mt-4 text-xs text-white/60">
              No spam, ever. Unsubscribe with one click.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
